import { homeCategoryKeys, type AdminHomeResponse, type HomeCategoryKey } from "@/types/home"

/**
 * Pure Home state machine — depends only on the zero-import `types/home` leaf,
 * so e2e/public specs can import it directly like `productsPageState.ts`.
 */

export type HomeMode = "onboarding" | "live"
export type HomeStep = 1 | 2 | 3 | 4

export type HomeState = {
  mode: HomeMode
  /** Meaningful in onboarding mode: the first not-yet-done step. */
  currentStep: HomeStep
  selectedCategories: HomeCategoryKey[]
  stepsDone: {
    category: boolean
    demo: boolean
    product: boolean
    publish: boolean
  }
  /** Sum of live (shopper-visible) products across all categories. */
  totalLive: number
  /** Sum of non-archived products across all categories. */
  totalProducts: number
}

export const deriveHomeState = (dto: AdminHomeResponse): HomeState => {
  const selected = dto.onboarding.selectedCategories
  const totalLive = dto.categories.reduce((sum, category) => sum + category.productsLive, 0)
  const totalProducts = dto.categories.reduce((sum, category) => sum + category.productsTotal, 0)
  const selectedTotal = dto.categories.reduce(
    (sum, category) => (selected.includes(category.key) ? sum + category.productsTotal : sum),
    0
  )

  const category = selected.length > 0
  // Step 2 keys off TOTAL, not live: glasses products are forced hidden until
  // digitization completes, yet the merchant did add them.
  const product = category && selectedTotal > 0
  // Backfill applies ONLY to merchants without an onboarding record
  // (derived=true): their live products predate the wizard and prove
  // demo/publish already happened. A merchant inside the wizard (record
  // exists) must actually perform steps 3-4 — a freshly saved makeup product
  // is live immediately and must not skip them. The backend materializes this
  // same rule into the record on its first write.
  const backfilled = dto.onboarding.derived && totalLive > 0
  const demo = product && (dto.onboarding.demoViewedAt !== null || backfilled)
  const publish = product && (dto.onboarding.publishConfirmedAt !== null || backfilled)

  return {
    currentStep: !category ? 1 : !product ? 2 : !demo ? 3 : 4,
    mode: category && product && demo && publish ? "live" : "onboarding",
    selectedCategories: selected,
    stepsDone: { category, demo, product, publish },
    totalLive,
    totalProducts,
  }
}

export const remainingCategories = (selected: readonly HomeCategoryKey[]): HomeCategoryKey[] =>
  homeCategoryKeys.filter((key) => !selected.includes(key))

export type HomePublishTarget = "qr" | "viewer3d-integration"

/**
 * Where the Publish step sends the merchant. Try-on products publish through
 * the QR/link modal; a 3D-only catalog publishes through the embed snippet on
 * the 3D Integration page — its try-on QR landing would be empty (product
 * decision on the Homepage ticket, 2026-08-05). Keys off TOTAL like the rest
 * of the wizard: hidden-until-digitized glasses still make a try-on merchant.
 */
export const derivePublishTarget = (dto: AdminHomeResponse): HomePublishTarget =>
  dto.categories.some((category) => category.key !== "viewer3d" && category.productsTotal > 0)
    ? "qr"
    : "viewer3d-integration"
